import {Injectable, OnDestroy} from '@angular/core';
import {AppDialogRef, AppDialogService} from '@app/theme';
import {TranslateService} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {RestaurantChangedDialogComponent} from './restaurant-changed-dialog.component';
import {SessionService} from './session.service';

@Injectable({
	providedIn: 'root',
})
export class RestaurantTabSyncService implements OnDestroy {
	private trackedRestaurantId: number | null = null;
	private modalOpen = false;
	private activeDialogRef: AppDialogRef | null = null;
	private restaurantChangeSub: Subscription;
	private readonly storageListener = (event: StorageEvent) => this.onStorage(event);
	private readonly visibilityListener = () => this.onVisibilityChange();

	constructor(
		private sessionService: SessionService,
		private dialogService: AppDialogService,
		private translate: TranslateService,
	) {}

	init(): void {
		this.restaurantChangeSub = this.sessionService.getRestaurantChangeEmitter().subscribe(restaurantId => {
			this.trackedRestaurantId = restaurantId ?? null;
			this.modalOpen = false;
		});

		this.syncTrackedRestaurantId();
		window.addEventListener('storage', this.storageListener);
		document.addEventListener('visibilitychange', this.visibilityListener);
	}

	ngOnDestroy(): void {
		this.restaurantChangeSub?.unsubscribe();
		window.removeEventListener('storage', this.storageListener);
		document.removeEventListener('visibilitychange', this.visibilityListener);
	}

	private onStorage(event: StorageEvent): void {
		if (event.key !== SessionService.SESSION_KEY && event.key !== 'groin_session') {
			return;
		}
		this.checkRestaurantDrift();
	}

	private onVisibilityChange(): void {
		if (document.visibilityState !== 'visible') {
			return;
		}
		this.checkRestaurantDrift();
	}

	private checkRestaurantDrift(): void {
		const currentRestaurantId = this.sessionService.getRestaurantId();
		if (this.isSameRestaurant(this.trackedRestaurantId, currentRestaurantId)) {
			return;
		}
		this.showReloadModal();
	}

	private showReloadModal(): void {
		if (this.modalOpen) {
			return;
		}

		this.modalOpen = true;
		const restaurant = this.sessionService.getRestaurant();

		this.activeDialogRef = this.dialogService.open(
			RestaurantChangedDialogComponent,
			{
				header: this.translate.instant('Restaurant modifié'),
				width: '28rem',
				closable: false,
				closeOnEsc: false,
				closeOnBackdropClick: false,
				context: {
					restaurantName: restaurant?.name ?? restaurant?.nom ?? '',
				},
			},
		);

		this.activeDialogRef.onClose.subscribe((result) => {
			this.modalOpen = false;
			this.activeDialogRef = null;
			if (result !== 'reloaded') {
				window.location.reload();
			}
		});
	}

	private syncTrackedRestaurantId(): void {
		this.trackedRestaurantId = this.sessionService.getRestaurantId();
	}

	private isSameRestaurant(left: number | null, right: number | null): boolean {
		return (left ?? null) === (right ?? null);
	}
}
